import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Bell, Mail, Clock, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useNotifications } from "@/contexts/NotificationContext";
import { toast } from "sonner";

const CATEGORIES = [
  { id: "general", label: "General" },
  { id: "us-politics", label: "US Politics" },
  { id: "business", label: "Business" },
  { id: "technology", label: "Technology" },
  { id: "sports", label: "Sports" },
  { id: "entertainment", label: "Entertainment" },
  { id: "health", label: "Health" },
  { id: "science", label: "Science" },
  { id: "us-weather", label: "US Weather" },
];

const FREQUENCIES = [
  { id: "instant", label: "Instant", description: "Get notified immediately for breaking news" },
  { id: "hourly", label: "Hourly", description: "Receive a summary every hour" },
  { id: "daily", label: "Daily", description: "Get a daily digest of top stories" },
];

const SettingsPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { preferences, updatePreferences } = useNotifications();
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [frequency, setFrequency] = useState("instant");
  const [emailEnabled, setEmailEnabled] = useState(true);
  const [inAppEnabled, setInAppEnabled] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user || user === false) { navigate("/"); return; }
    if (preferences) {
      setSelectedCategories(preferences.categories || []);
      setFrequency(preferences.frequency || "instant");
      setEmailEnabled(preferences.email_enabled !== false);
      setInAppEnabled(preferences.in_app_enabled !== false);
    }
  }, [user, preferences, navigate]);

  const toggleCategory = (categoryId) => {
    setSelectedCategories(prev => prev.includes(categoryId) ? prev.filter(c => c !== categoryId) : [...prev, categoryId]);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await updatePreferences({ categories: selectedCategories, frequency, email_enabled: emailEnabled, in_app_enabled: inAppEnabled });
      toast.success("Notification preferences updated!");
    } catch (error) { toast.error("Failed to update preferences"); }
    finally { setLoading(false); }
  };

  const handleLogout = async () => { await logout(); navigate("/"); };

  if (!user || user === false) return null;

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--color-bg)" }}>
      <div className="max-w-4xl mx-auto px-4 py-8">
        <button onClick={() => navigate("/")} className="flex items-center gap-2 mb-6 transition-colors" style={{ color: "var(--color-text-dim)" }}>
          <ArrowLeft className="w-5 h-5" />
          Back to News
        </button>

        <div className="rounded-xl p-8" style={{ backgroundColor: "var(--color-card)", border: "1px solid var(--color-border)" }}>
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-black mb-2" style={{ color: "var(--color-text)", fontFamily: "Cabinet Grotesk, sans-serif" }}>
                Notification Settings
              </h1>
              <p style={{ color: "var(--color-text-muted)" }}>Customize your news notifications</p>
            </div>
            <div className="flex items-center gap-2">
              {user.role === "admin" && (
                <Button onClick={() => navigate("/admin")} className="text-white" style={{ backgroundColor: "var(--color-accent)" }}>
                  Admin Dashboard
                </Button>
              )}
              <Button onClick={handleLogout} variant="outline" style={{ borderColor: "var(--color-border)", color: "var(--color-text-muted)" }}>
                Logout
              </Button>
            </div>
          </div>

          {/* User Info */}
          <div className="mb-8 p-4 rounded-lg" style={{ backgroundColor: "var(--color-card-hover)" }}>
            <p className="text-sm" style={{ color: "var(--color-text-dim)" }}>Signed in as</p>
            <p className="font-semibold" style={{ color: "var(--color-text)" }}>{user.name}</p>
            <p className="text-sm" style={{ color: "var(--color-text-dim)" }}>{user.email}</p>
          </div>

          {/* Notification Channels */}
          <div className="mb-8">
            <h2 className="text-xl font-bold mb-4" style={{ color: "var(--color-text)" }}>Notification Channels</h2>
            <div className="space-y-4">
              <label className="flex items-center gap-3 p-4 rounded-lg cursor-pointer transition-colors" style={{ backgroundColor: "var(--color-card-hover)" }}>
                <input type="checkbox" checked={inAppEnabled} onChange={(e) => setInAppEnabled(e.target.checked)} className="w-5 h-5" />
                <Bell className="w-5 h-5" style={{ color: "var(--color-accent)" }} />
                <div className="flex-1">
                  <p className="font-medium" style={{ color: "var(--color-text)" }}>In-App Notifications</p>
                  <p className="text-sm" style={{ color: "var(--color-text-dim)" }}>Get notified within the app</p>
                </div>
              </label>
              <label className="flex items-center gap-3 p-4 rounded-lg cursor-pointer transition-colors" style={{ backgroundColor: "var(--color-card-hover)" }}>
                <input type="checkbox" checked={emailEnabled} onChange={(e) => setEmailEnabled(e.target.checked)} className="w-5 h-5" />
                <Mail className="w-5 h-5" style={{ color: "var(--color-live)" }} />
                <div className="flex-1">
                  <p className="font-medium" style={{ color: "var(--color-text)" }}>Email Notifications</p>
                  <p className="text-sm" style={{ color: "var(--color-text-dim)" }}>Receive updates via email</p>
                </div>
              </label>
            </div>
          </div>

          {/* Frequency */}
          <div className="mb-8">
            <h2 className="text-xl font-bold mb-4 flex items-center gap-2" style={{ color: "var(--color-text)" }}>
              <Clock className="w-5 h-5" />
              Notification Frequency
            </h2>
            <div className="space-y-3">
              {FREQUENCIES.map((freq) => (
                <label
                  key={freq.id}
                  className="flex items-start gap-3 p-4 rounded-lg cursor-pointer transition-colors"
                  style={{
                    backgroundColor: frequency === freq.id ? "var(--color-accent-glow)" : "var(--color-card-hover)",
                    border: frequency === freq.id ? "1px solid var(--color-accent)" : "1px solid transparent",
                  }}
                >
                  <input type="radio" name="frequency" value={freq.id} checked={frequency === freq.id} onChange={(e) => setFrequency(e.target.value)} className="mt-1" />
                  <div>
                    <p className="font-medium" style={{ color: "var(--color-text)" }}>{freq.label}</p>
                    <p className="text-sm" style={{ color: "var(--color-text-dim)" }}>{freq.description}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Categories */}
          <div className="mb-8">
            <h2 className="text-xl font-bold mb-4" style={{ color: "var(--color-text)" }}>News Categories</h2>
            <p className="text-sm mb-4" style={{ color: "var(--color-text-dim)" }}>Select categories you want to receive notifications for</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {CATEGORIES.map((category) => (
                <label
                  key={category.id}
                  className="flex items-center gap-2 p-3 rounded-lg cursor-pointer transition-colors"
                  style={{
                    backgroundColor: selectedCategories.includes(category.id) ? "var(--color-accent)" : "var(--color-card-hover)",
                    color: selectedCategories.includes(category.id) ? "#fff" : "var(--color-text-muted)",
                  }}
                >
                  <input type="checkbox" checked={selectedCategories.includes(category.id)} onChange={() => toggleCategory(category.id)} className="sr-only" />
                  <span className="font-medium text-sm">{category.label}</span>
                </label>
              ))}
            </div>
          </div>

          <Button
            onClick={handleSave}
            disabled={loading}
            className="w-full py-3 rounded-lg font-bold flex items-center justify-center gap-2 text-white"
            style={{ backgroundColor: "var(--color-accent)" }}
          >
            <Save className="w-5 h-5" />
            {loading ? "Saving..." : "Save Preferences"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
